import * as R from "ramda";

import {
  FETCH_OPPORTUNITY,
  SAVE_OPPORTUNITY_USERS,
  SAVE_OPPORTUNITY_DETAILS,
  SAVE_OPPORTUNITY_MEDIA,
  SAVE_OPPORTUNITY_STEPS,
  SAVE_OPPORTUNITY_CANDIDATE,
  SAVE_OPPORTUNITY_QUESTIONS,
  SAVE_OPPORTUNITY_MULTIPOSTING,
  SAVE_MULTIPOSTING_FORM,
  SAVE_SCRAPPER_FORM,
  FETCH_OPPORTUNITY_ROOMS,
  UPDATE_OPPORTUNITY_STEP,
  FETCH_CHATBOT_CREATED,
  START_CHATBOT_TEST_MESSAGES,
  NEW_CHATBOT_TEST_MESSAGE,
  SEND_CHATBOT_TEST_MESSAGE,
  PUBLISH_OPPORTUNITY,
  UNPUBLISH_OPPORTUNITY,
  CLOSE_OPPORTUNITY,
  NEW_OPPORTUNITY,
  DESELECT_OPPORTUNITY,
  GO_TO_PREVIOUS_WIZARD_STEP,
  UPDATE_OPPORTUNITY,
  PUBLISH_OPPORTUNITY_FEED,
  SAVE_MULTIPOSTING_UNIVERSITIES_FORM,
  SAVE_SHAREDEMAIL_FORM,
  GENERATE_EMAIL_OPPORTUNITY,
  SAVE_AUTO_INVITE_EXTERNAL_CANDIDATES,
  GO_TO_NEXT_WIZARD_STEP,
  MARK_CANDIDATES_AS_READ,
} from "../actions/opportunity";
import { SELECT_COMPANY } from "../actions/company";
import opportunityDefaults from "../../utils/data/static/opportunityDefaults";
import { formatMultiposting } from "../../scenes/Opportunity/routes/OpportunityDetails/MultipostingForm/selector";

const initialState = {
  newOpportunity: false,
  selected: {},
  step: 0,
};

const opportunity = (state = initialState, { type, payload = {} }) => {
  switch (type) {
    case FETCH_OPPORTUNITY.RESOLVED:
      return { ...state, newOpportunity: false, selected: payload };

    case FETCH_OPPORTUNITY_ROOMS.RESOLVED:
      return R.evolve({ selected: R.assoc("selectedStep", payload) })(state);

    // When updating step we should restart the chatbot created prop
    case UPDATE_OPPORTUNITY_STEP.STARTED:
      return R.assocPath(
        ["selected", "selectedStep", "isChatbotCreated"],
        false,
        state,
      );

    case FETCH_CHATBOT_CREATED.RESOLVED:
      return R.assocPath(
        ["selected", "selectedStep", "isChatbotCreated"],
        payload,
        state,
      );

    case NEW_CHATBOT_TEST_MESSAGE.TYPE:
      return R.over(
        R.lensPath(["selected", "selectedStep", "messages"]),
        R.pipe(R.defaultTo([]), R.append(payload)),
        state,
      );

    case SEND_CHATBOT_TEST_MESSAGE.STARTED:
      return R.over(
        R.lensPath(["selected", "selectedStep", "messages"]),
        R.pipe(
          R.defaultTo([]),
          R.append({
            source: "candidate",
            text: payload,
          }),
        ),
        state,
      );

    case START_CHATBOT_TEST_MESSAGES.STARTED:
      return R.set(
        R.lensPath(["selected", "selectedStep", "messages"]),
        [],
        state,
      );

    case NEW_OPPORTUNITY.TYPE:
      const { company } = payload;

      let opportunityStorage;

      try {
        opportunityStorage = JSON.parse(localStorage.getItem("opportunity"));

        if (company !== R.propOr(null, "company", opportunityStorage)) {
          opportunityStorage = {};
          localStorage.removeItem("opportunity");
        }
      } catch (error) {
        localStorage.removeItem("opportunity");
      }

      const selected = R.propOr(
        opportunityDefaults,
        "selected",
        opportunityStorage,
      );

      const step = R.propOr(0, "step", opportunityStorage);

      return {
        ...state,
        company: payload.company,
        newOpportunity: true,
        selected,
        step,
      };

    // Add "deleted" flag to show loading indicator
    case SAVE_OPPORTUNITY_USERS.STARTED: {
      return R.evolve({
        selected: {
          guest: R.map(
            R.when(
              R.propEq("id", payload.deletedUser),
              R.assoc("deleted", true),
            ),
          ),
        },
      })(state);
    }

    case UPDATE_OPPORTUNITY.RESOLVED:
    case SAVE_OPPORTUNITY_USERS.RESOLVED:
    case SAVE_OPPORTUNITY_DETAILS.RESOLVED:
    case SAVE_OPPORTUNITY_MEDIA.RESOLVED:
    case SAVE_OPPORTUNITY_CANDIDATE.RESOLVED:
    case SAVE_OPPORTUNITY_MULTIPOSTING.RESOLVED:
    case UNPUBLISH_OPPORTUNITY.RESOLVED:
    case SAVE_MULTIPOSTING_UNIVERSITIES_FORM.RESOLVED: {
      const data = state.newOpportunity
        ? {
            ...state,
            selected: { ...state.selected, ...payload },
            step: state.step + 1,
          }
        : { ...state, selected: { ...state.selected, ...payload } };

      state.newOpportunity &&
        localStorage.setItem("opportunity", JSON.stringify(data));
      return data;
    }

    case SAVE_OPPORTUNITY_QUESTIONS.RESOLVED:
    case SAVE_OPPORTUNITY_STEPS.RESOLVED: {
      const data = { ...state, selected: { ...state.selected, ...payload } };

      state.newOpportunity &&
        localStorage.setItem("opportunity", JSON.stringify(data));
      return data;
    }

    case GO_TO_NEXT_WIZARD_STEP.TYPE: {
      const data = { ...state, step: state.step + 1 };
      localStorage.setItem("opportunity", JSON.stringify(data));
      return data;
    }

    case GO_TO_PREVIOUS_WIZARD_STEP.TYPE: {
      const data = { ...state, step: state.step - 1 };
      localStorage.setItem("opportunity", JSON.stringify(data));
      return data;
    }
    case PUBLISH_OPPORTUNITY_FEED.STARTED:
      return {
        ...state,
        loadingSite: payload,
      };
    case PUBLISH_OPPORTUNITY_FEED.RESOLVED:
      return {
        ...R.dissoc("loadingSite", state),
        selected: { ...state.selected, ...R.dissoc("feed", payload) },
      };
    case PUBLISH_OPPORTUNITY.RESOLVED:
      return { ...state, selected: { ...state.selected, ...payload } };
    case SAVE_MULTIPOSTING_FORM.TYPE:
      const multiposting = formatMultiposting(
        R.F,
        (provider) => payload[provider],
        ["facebook", "linkedin"].filter(
          (provider) => payload[`${provider}Social`],
        ),
      );

      return { ...state, selected: { ...state.selected, multiposting } };

    case SAVE_AUTO_INVITE_EXTERNAL_CANDIDATES.TYPE:
      return {
        ...state,
        selected: { ...state.selected, autoInviteExternalCandidates: payload },
      };
    case SAVE_SCRAPPER_FORM.TYPE:
      return { ...state, selected: { ...state.selected, scrapper: payload } };
    case SAVE_MULTIPOSTING_UNIVERSITIES_FORM.TYPE:
      if (!payload || !payload.length) return state;
      return {
        ...state,
        selected: { ...state.selected, publishedOnCompanies: payload },
      };

    case SAVE_SHAREDEMAIL_FORM.TYPE:
      return {
        ...state,
        selected: { ...state.selected, createJobPostEmail: payload },
      };

    case GENERATE_EMAIL_OPPORTUNITY.RESOLVED:
      return {
        ...state,
        selected: {
          ...state.selected,
          ...payload,
        },
      };
    case SELECT_COMPANY.TYPE:
    case DESELECT_OPPORTUNITY.TYPE:
    case CLOSE_OPPORTUNITY.RESOLVED:
      return initialState;

    case MARK_CANDIDATES_AS_READ.RESOLVED:
      const { candidates, view } = payload;
      let views = R.pathOr([], ["selected", "views"], state);

      if (view) {
        views = R.union(views, candidates);
      } else {
        views = R.without(candidates, views);
      }

      return {
        ...state,
        selected: {
          ...state.selected,
          views,
        },
      };

    default:
      return state;
  }
};

export default opportunity;
